import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import VideoCard from '@/components/feature/VideoCard';
import FilterSidebar from '@/pages/category/components/FilterSidebar';
import { videoList, typeLabels, typeIntroductions, subTypesByType, sortOptions } from '@/mocks/videos';
import { filterVideos, sortVideos, type FilterState } from '@/utils/video';

const PAGE_SIZE = 8;

const defaultFilters: FilterState = { subType: '全部', region: '全部', year: '全部', popularity: '全部' };

export default function CategoryPage() {
  const { type = 'movie' } = useParams<{ type: string }>();
  const validType = ['movie', 'tv', 'variety'].includes(type) ? type : 'movie';
  const typeLabel = typeLabels[validType] || '全部';
  const subTypes = subTypesByType[validType] || ['全部'];

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [sortKey, setSortKey] = useState('latest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    const base = filterVideos(videoList, { ...filters, type: validType });
    return sortVideos(base, sortKey);
  }, [filters, sortKey, validType]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const updateFilters = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setVisibleCount(PAGE_SIZE);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setVisibleCount(PAGE_SIZE);
  };

  useEffect(() => {
    document.title = `${typeLabel} - 在线免费观看`;
  }, [typeLabel]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
          <Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{typeLabel}</span>
        </nav>

        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">{typeLabel}</h1>
        <p className="text-sm text-foreground-600 max-w-2xl mb-6">{typeIntroductions[validType]}</p>

        <button
          type="button"
          onClick={() => setMobileFilterOpen((v) => !v)}
          className="lg:hidden flex items-center gap-2 mb-4 px-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line text-primary-600"></i>
          筛选条件
          <i className={mobileFilterOpen ? 'ri-arrow-up-s-line ml-auto' : 'ri-arrow-down-s-line ml-auto'}></i>
        </button>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className={`${mobileFilterOpen ? 'block' : 'hidden'} lg:block`}>
            <FilterSidebar filters={filters} subTypes={subTypes} onChange={updateFilters} onReset={resetFilters} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <span className="text-sm text-foreground-500">
                共找到 <span className="text-primary-700 font-semibold">{filtered.length}</span> 部内容
              </span>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSortKey(option.key)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sortKey === option.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {visible.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {visible.map((video) => (
                    <VideoCard key={video.id} video={video} showDescription />
                  ))}
                </div>

                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                      className="px-8 h-11 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      加载更多
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-3xl"></i>
                </span>
                <p className="text-foreground-700 font-medium mb-1">暂无符合条件的内容</p>
                <p className="text-sm text-foreground-500 mb-5">试试调整筛选条件，或许能发现更多惊喜</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-6 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}